<script setup lang="ts">
import { del, get, post, put } from '@/api'

definePage({
  meta: {
    action: 'manage',
    subject: 'Auth',
  },
})

const { snackbar } = useErrorHandler()

// OAuth 클라이언트 인터페이스
interface OAuthClient {
  id: number
  name: string
  client_id: string
  redirect_uri: string
  scopes: string[]
  is_active: boolean
  created_at: string
}

// 상태
const clients = ref<OAuthClient[]>([])
const loading = ref(false)
const saving = ref(false)
const deleteLoading = ref<number | null>(null)
const regenerateLoading = ref<number | null>(null)

// 다이얼로그
const createDialog = ref(false)
const newSecretDialog = ref(false)
const newClientId = ref('')
const newClientSecret = ref('')

// 폼 데이터
const formData = ref({
  name: '',
  redirect_uri: 'https://claude.ai/api/mcp/auth_callback',
  scopes: ['*'] as string[],
})

// 테이블 헤더
const headers = [
  { title: '이름', key: 'name', width: '180px' },
  { title: 'Client ID', key: 'client_id', width: '280px' },
  { title: 'Redirect URI', key: 'redirect_uri', width: '280px' },
  { title: '스코프', key: 'scopes', width: '120px' },
  { title: '상태', key: 'is_active', width: '100px' },
  { title: '생성일', key: 'created_at', width: '150px' },
  { title: '액션', key: 'actions', width: '180px', sortable: false },
]

// 날짜 포맷
const formatDate = (date: string | null): string => {
  if (!date)
    return '-'

  return new Date(date).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// 클라이언트 목록 로드
const loadClients = async () => {
  loading.value = true
  try {
    const { success, data } = await get<{ data: OAuthClient[] }>('/oauth-clients')
    if (success && data)
      clients.value = data.data
  }
  catch (error) {
    console.error('Failed to load OAuth clients:', error)
    showNotification('OAuth 클라이언트 목록을 불러오는데 실패했습니다.', 'error')
  }
  finally {
    loading.value = false
  }
}

// 새 클라이언트 생성
const createClient = async () => {
  if (!formData.value.name.trim()) {
    showNotification('클라이언트 이름을 입력해주세요.', 'warning')

    return
  }

  if (!formData.value.redirect_uri.trim()) {
    showNotification('Redirect URI를 입력해주세요.', 'warning')

    return
  }

  saving.value = true
  try {
    const payload = {
      name: formData.value.name,
      redirect_uri: formData.value.redirect_uri,
      scopes: formData.value.scopes,
    }

    const { success, data } = await post<{
      message: string
      client_id: string
      client_secret: string
      data: OAuthClient
    }>('/oauth-clients', payload)

    if (success && data) {
      createDialog.value = false
      newClientId.value = data.client_id
      newClientSecret.value = data.client_secret
      newSecretDialog.value = true
      resetForm()
      await loadClients()
      showNotification('OAuth 클라이언트가 생성되었습니다.')
    }
    else {
      showNotification('OAuth 클라이언트 생성에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to create OAuth client:', error)
    showNotification('OAuth 클라이언트 생성에 실패했습니다.', 'error')
  }
  finally {
    saving.value = false
  }
}

// 클라이언트 활성화/비활성화 토글
const toggleActive = async (client: OAuthClient) => {
  try {
    const { success } = await put(`/oauth-clients/${client.id}`, { is_active: !client.is_active })

    if (success) {
      client.is_active = !client.is_active
      showNotification(client.is_active ? '클라이언트가 활성화되었습니다.' : '클라이언트가 비활성화되었습니다.')
    }
    else {
      showNotification('상태 변경에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to toggle OAuth client:', error)
    showNotification('상태 변경에 실패했습니다.', 'error')
  }
}

// 클라이언트 삭제
const deleteClient = async (client: OAuthClient) => {
  if (!confirm(`"${client.name}" 클라이언트를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없으며, 관련된 모든 토큰이 무효화됩니다.`))
    return

  deleteLoading.value = client.id
  try {
    const { success } = await del(`/oauth-clients/${client.id}`)

    if (success) {
      await loadClients()
      showNotification('OAuth 클라이언트가 삭제되었습니다.')
    }
    else {
      showNotification('삭제에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to delete OAuth client:', error)
    showNotification('삭제에 실패했습니다.', 'error')
  }
  finally {
    deleteLoading.value = null
  }
}

// Client Secret 재발급
const regenerateSecret = async (client: OAuthClient) => {
  if (!confirm(`"${client.name}" 클라이언트의 Secret을 재발급하시겠습니까?\n기존 Secret은 즉시 무효화됩니다.`))
    return

  regenerateLoading.value = client.id
  try {
    const { success, data } = await post<{ message: string; client_secret: string }>(`/oauth-clients/${client.id}/regenerate-secret`)

    if (success && data) {
      newClientId.value = client.client_id
      newClientSecret.value = data.client_secret
      newSecretDialog.value = true
      showNotification('Client Secret이 재발급되었습니다.')
    }
    else {
      showNotification('재발급에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to regenerate client secret:', error)
    showNotification('재발급에 실패했습니다.', 'error')
  }
  finally {
    regenerateLoading.value = null
  }
}

// 복사 기능
const copyToClipboard = async (text: string, label: string) => {
  try {
    await navigator.clipboard.writeText(text)
    showNotification(`${label}이(가) 클립보드에 복사되었습니다.`)
  }
  catch {
    // 폴백
    const input = document.createElement('input')

    input.value = text
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    showNotification(`${label}이(가) 클립보드에 복사되었습니다.`)
  }
}

// 폼 리셋
const resetForm = () => {
  formData.value = {
    name: '',
    redirect_uri: 'https://claude.ai/api/mcp/auth_callback',
    scopes: ['*'],
  }
}

// 알림 표시
const showNotification = (message: string, color: 'success' | 'error' | 'warning' | 'info' = 'success') => {
  snackbar.show(message, color)
}

// 초기 로드
onMounted(() => {
  loadClients()
})
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>OAuth 클라이언트 관리</span>
          <div class="d-flex gap-2">
            <VBtn
              icon="tabler-refresh"
              variant="text"
              :loading="loading"
              @click="loadClients"
            />
            <VBtn
              color="primary"
              prepend-icon="tabler-plus"
              @click="createDialog = true"
            >
              새 클라이언트
            </VBtn>
          </div>
        </VCardTitle>

        <VCardText>
          <VAlert
            type="info"
            variant="tonal"
            class="mb-4"
          >
            Claude.ai 웹 버전에서 MCP 서버에 연결할 때 사용하는 OAuth 클라이언트를 관리합니다.
            Client Secret은 생성 시 한 번만 표시되므로 안전하게 보관하세요.
          </VAlert>

          <VDataTable
            :headers="headers"
            :items="clients"
            :loading="loading"
            density="compact"
            :items-per-page="10"
          >
            <template #item.client_id="{ item }">
              <div class="d-flex align-center gap-1">
                <code class="text-caption">{{ item.client_id }}</code>
                <VBtn
                  icon="tabler-copy"
                  size="x-small"
                  variant="text"
                  @click="copyToClipboard(item.client_id, 'Client ID')"
                />
              </div>
            </template>

            <template #item.redirect_uri="{ item }">
              <code class="text-caption">{{ item.redirect_uri }}</code>
            </template>

            <template #item.scopes="{ item }">
              <VChip
                v-for="scope in item.scopes"
                :key="scope"
                size="small"
                class="me-1"
              >
                {{ scope }}
              </VChip>
            </template>

            <template #item.is_active="{ item }">
              <VSwitch
                :model-value="item.is_active"
                hide-details
                density="compact"
                color="success"
                @update:model-value="toggleActive(item)"
              />
            </template>

            <template #item.created_at="{ item }">
              {{ formatDate(item.created_at) }}
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex gap-1">
                <VBtn
                  icon="tabler-refresh"
                  size="small"
                  variant="text"
                  color="warning"
                  :loading="regenerateLoading === item.id"
                  title="Secret 재발급"
                  @click="regenerateSecret(item)"
                />
                <VBtn
                  icon="tabler-trash"
                  size="small"
                  variant="text"
                  color="error"
                  :loading="deleteLoading === item.id"
                  title="삭제"
                  @click="deleteClient(item)"
                />
              </div>
            </template>

            <template #no-data>
              <div class="text-center py-4">
                <VIcon
                  icon="tabler-brand-oauth"
                  size="48"
                  color="grey"
                  class="mb-2"
                />
                <p class="text-grey">
                  등록된 OAuth 클라이언트가 없습니다.
                </p>
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 새 클라이언트 생성 다이얼로그 -->
    <VDialog
      v-model="createDialog"
      max-width="600"
      persistent
    >
      <VCard>
        <VCardTitle>새 OAuth 클라이언트 생성</VCardTitle>
        <VCardText>
          <VTextField
            v-model="formData.name"
            label="클라이언트 이름"
            placeholder="예: Claude.ai Production"
            required
            class="mb-4"
          />

          <VTextField
            v-model="formData.redirect_uri"
            label="Redirect URI"
            placeholder="https://claude.ai/api/mcp/auth_callback"
            hint="Claude.ai 웹 버전의 경우 https://claude.ai/api/mcp/auth_callback 을 사용하세요"
            persistent-hint
            class="mb-4"
          />

          <VSelect
            v-model="formData.scopes"
            label="스코프"
            :items="[
              { title: '전체 권한', value: '*' },
              { title: '읽기 전용', value: 'read' },
              { title: '리뷰 관리', value: 'review:*' },
            ]"
            multiple
            chips
            closable-chips
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="createDialog = false; resetForm()"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="createClient"
          >
            생성
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 새 클라이언트 정보 표시 다이얼로그 -->
    <VDialog
      v-model="newSecretDialog"
      max-width="700"
      persistent
    >
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon
            icon="tabler-shield-check"
            color="success"
            class="me-2"
          />
          OAuth 클라이언트가 생성되었습니다
        </VCardTitle>
        <VCardText>
          <VAlert
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            Client Secret은 다시 표시되지 않습니다. 반드시 안전한 곳에 복사해서 보관하세요.
          </VAlert>

          <div class="mb-4">
            <label class="text-subtitle-2 d-block mb-1">Client ID</label>
            <VTextField
              :model-value="newClientId"
              readonly
              variant="outlined"
              density="compact"
            >
              <template #append>
                <VBtn
                  icon="tabler-copy"
                  variant="text"
                  size="small"
                  @click="copyToClipboard(newClientId, 'Client ID')"
                />
              </template>
            </VTextField>
          </div>

          <div class="mb-4">
            <label class="text-subtitle-2 d-block mb-1">Client Secret</label>
            <VTextField
              :model-value="newClientSecret"
              readonly
              variant="outlined"
              density="compact"
              class="font-weight-bold"
            >
              <template #append>
                <VBtn
                  icon="tabler-copy"
                  variant="text"
                  size="small"
                  @click="copyToClipboard(newClientSecret, 'Client Secret')"
                />
              </template>
            </VTextField>
          </div>

          <VDivider class="my-4" />

          <div>
            <p class="text-subtitle-2 mb-2">
              Claude.ai Custom Connector 설정:
            </p>
            <VAlert
              type="info"
              variant="tonal"
              class="mb-3"
            >
              Claude.ai 설정 → MCP → Remote Server → Custom OAuth 에서 아래 정보를 입력하세요.
            </VAlert>
            <VTable density="compact">
              <tbody>
                <tr>
                  <td class="text-no-wrap font-weight-medium">
                    MCP Server URL
                  </td>
                  <td>
                    <code>https://mcp-server.regle.co.kr/mcp</code>
                  </td>
                </tr>
                <tr>
                  <td class="text-no-wrap font-weight-medium">
                    Authorization URL
                  </td>
                  <td>
                    <code>https://mcp-server.regle.co.kr/oauth/authorize</code>
                  </td>
                </tr>
                <tr>
                  <td class="text-no-wrap font-weight-medium">
                    Token URL
                  </td>
                  <td>
                    <code>https://mcp-server.regle.co.kr/oauth/token</code>
                  </td>
                </tr>
                <tr>
                  <td class="text-no-wrap font-weight-medium">
                    Client ID
                  </td>
                  <td>
                    <code>{{ newClientId }}</code>
                  </td>
                </tr>
                <tr>
                  <td class="text-no-wrap font-weight-medium">
                    Client Secret
                  </td>
                  <td>
                    <code>{{ newClientSecret }}</code>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="primary"
            @click="newSecretDialog = false; newClientId = ''; newClientSecret = ''"
          >
            확인
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </VRow>
</template>

<style scoped>
pre {
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
